/**
 * CustomerAcceptedSheet — customer-side confirmation slide-up when a pro accepts
 * their shared-service request (ride, taxi, motor, tricycle, van, delivery, runner,
 * laundry, trash). Shows who's coming and a "Track" button into the live trip.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, MapPin, Navigation, Phone, Clock } from "lucide-react";
import { useCustomerTripEvents, seenSet, markSeen } from "@/lib/useCustomerTripEvents";
import PersistentSheet from "@/components/requests/PersistentSheet";
import { SERVICE_TONES, SERVICE_LABELS, serviceToneKey } from "@/lib/serviceTones";
import { serviceKind } from "@/lib/cancelReasons";
import { term } from "@/utils/rideshareTerms";
import { tripFare } from "@/utils/tripFare";
import { playSoftChime } from "@/lib/chime";

const SEEN_KEY = "yorm:cust-accepted-seen";
const ACCEPT_TYPES = ["rideshare_accepted", "offer_accepted"];

export const proNoun = (b) => {
  if (b?.service_flavor === "trash") return "collector";
  if (b?.service_flavor === "laundry") return b.pro_role || "rider";
  const k = serviceToneKey(b);
  if (k === "van") return "driver";
  if (k === "taxi") return "taxi driver";
  if (k === "tricycle") return "tricycle rider";
  return term(b?.rideshare_type);
};
const headline = (b) => {
  const kind = serviceKind(b);
  if (kind === "laundry_from_customer") return "Your rider is coming for the laundry";
  if (kind === "laundry_from_shop") return "Your rider is bringing the clean clothes";
  if (kind === "trash") return "Your trash pickup is confirmed";
  if (kind === "runner") return "Your runner is on the way";
  if (kind === "delivery") return "Your courier is on the way";
  if (kind === "van") return "Your van is booked";
  return `Your ${proNoun(b)} is on the way`;
};

export default function CustomerAcceptedSheet({ demo }) {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(demo || null);
  useCustomerTripEvents(ACCEPT_TYPES, (b, { onPage }) => {
    if (b.status !== "accepted" || !b.artisan_user_id || onPage || seenSet(SEEN_KEY).has(b.id)) return;
    markSeen(SEEN_KEY, b.id);
    playSoftChime();
    setTrip(b);
  });

  if (!trip) return null;
  const tone = SERVICE_TONES[serviceToneKey(trip)];
  const label = SERVICE_LABELS[serviceToneKey(trip)] || "Trip";
  const fare = tripFare(trip);
  const km = trip.estimated_distance_km != null ? Number(trip.estimated_distance_km).toFixed(1) : null;
  const pro = trip.artisan_name || `Your ${proNoun(trip)}`;
  const v = trip.driver_vehicle || {};
  const vehicle = [v.color, v.make, v.model].filter(Boolean).join(" ");
  const track = () => { const id = trip.id; setTrip(null); navigate(`/customer/trip/${id}`); };

  return (
    <PersistentSheet open title={`${label} accepted`} icon={Check} testId="customer-accepted-sheet" className="pay-glass">
      <div className="space-y-4" style={{ "--svc": tone.main, "--svc-dark": tone.dark }}>
        <div className="flex flex-col items-center text-center pt-1">
          <span className="h-14 w-14 rounded-full flex items-center justify-center text-white text-[22px] font-bold shadow-md animate-in zoom-in-50 duration-300" style={{ background: tone.main }} data-testid="customer-accepted-avatar">
            {(trip.artisan_name || "Y").trim().charAt(0).toUpperCase()}
          </span>
          <p className="mt-3 text-[17px] font-semibold text-[#222]" data-testid="customer-accepted-headline">{headline(trip)}</p>
          <p className="text-[13px] text-gray-500" data-testid="customer-accepted-sub">
            {pro} accepted your {label.toLowerCase()} request{vehicle ? ` · ${vehicle}` : ""}{v.plate_number ? ` · ${v.plate_number}` : ""}
          </p>
        </div>

        <div className="rounded-2xl bg-white/80 p-4 space-y-3" data-testid="customer-accepted-summary">
          <div className="flex gap-3">
            <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full flex items-center justify-center text-white" style={{ background: tone.main }}><MapPin size={13} /></span>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Pickup</p>
              <p className="text-[14px] text-[#222] leading-snug" data-testid="customer-accepted-pickup">{trip.pickup_address || "Pinned location"}</p>
            </div>
          </div>
          {trip.dropoff_address && (
            <div className="flex gap-3">
              <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#222] flex items-center justify-center text-white"><Navigation size={13} /></span>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-gray-500">Drop-off</p>
                <p className="text-[14px] text-[#222] leading-snug" data-testid="customer-accepted-dropoff">{trip.dropoff_address}</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between pt-1 border-t border-gray-100">
            <span className="text-[13px] text-gray-500 inline-flex items-center gap-1"><Clock size={13} />{km ? `${km} km` : "Live on the map"}</span>
            {fare != null && <span className="text-[16px] font-bold text-[#222]" data-testid="customer-accepted-fare">GHS {fare.toFixed(2)}</span>}
          </div>
        </div>

        <Button onClick={track} className="w-full h-12 rounded-xl text-white text-[16px] font-semibold bg-[var(--svc)] hover:bg-[var(--svc-dark)]" data-testid="customer-accepted-track-btn">
          <Navigation size={18} className="mr-2" /> Track my {proNoun(trip)}
        </Button>
        <div className="flex items-center justify-center gap-4 pb-1">
          {(trip.driver_phone || trip.artisan_phone) && (
            <a href={`tel:${(trip.driver_phone || trip.artisan_phone)}`} className="text-[15px] font-medium text-[var(--svc-dark)] inline-flex items-center gap-1" data-testid="customer-accepted-call">
              <Phone size={15} /> Call
            </a>
          )}
          <button type="button" onClick={() => setTrip(null)} className="text-[15px] font-medium text-gray-500" data-testid="customer-accepted-later-btn">Later</button>
        </div>
      </div>
    </PersistentSheet>
  );
}
